import * as React from 'react';
import SeasonParticipant from 'models/season-participant';
import { AuthContext } from 'auth/dist/contexts';
import { hasPermissionForProgram, Permission } from 'core/permissions';
import { APP_URL, TEAMSNAP_URL } from '../../core/constants';
import { Button, Icon, PopupAction } from '@teamsnap/teamsnap-ui';
import { User } from 'auth/dist/contexts/auth_context';

interface Props {
  seasonParticipant: SeasonParticipant;
  orgId?: number;
}

const joinParticipationRoles = (participationRoles) =>
  participationRoles?.map((participationRole) => participationRole.name).join(', ');

const ParticipationInfo: React.FunctionComponent<Props> = ({ seasonParticipant, orgId }) => {
  const context = React.useContext(AuthContext);
  const teamName = seasonParticipant.team?.name || 'No Team';
  const season = seasonParticipant.season as any; // TODO: give this the correct type so that {season?.program?.name} works
  const editURL = `${TEAMSNAP_URL}/${seasonParticipant.division?.id}/league_roster/player/${seasonParticipant.seasonParticipantId}`;
  const registration = seasonParticipant.registration;
  const participantRoles = joinParticipationRoles(seasonParticipant.participationRoles);
  const participantRolesDisplay = participantRoles ? participantRoles : '-';
  const inviteToJoinLink = `${TEAMSNAP_URL}/${seasonParticipant.division?.id}/league_roster/invite_all?player_id=${seasonParticipant.seasonParticipantId}`;
  const isSeasonArchived = season?.isArchived;

  const canEditParticipation = hasPermissionForProgram(
    context.user as User,
    Permission.MembersUpdate,
    `${orgId}`,
    `${season?.program?.id}`
  );
  const shouldLinkRegistration = hasPermissionForProgram(
    context.user as User,
    Permission.SeasonRegistrationView,
    `${orgId}`,
    `${season?.program?.id}`
  );

  const inviteToJoin = (seasonParticipant: SeasonParticipant, inviteToJoinLink: string) => {
    const isInvitable = seasonParticipant.invitationStatus === 'is_invitable';
    if (isInvitable) {
      return (
        <div>
          <a href={inviteToJoinLink}>
            <Button className="Button Button--primary Button--small u-spaceTopSm">Invite to Join</Button>
          </a>
        </div>
      );
    }
  };

  let registrationFormElem = <>{'-'}</>;
  if (registration?.registrationFormName && shouldLinkRegistration) {
    registrationFormElem = (
      <a
        href={`${
          registration.orderId
            ? `/organizations/${orgId}/financials/order/${registration.orderNumber}/details`
            : registration.registrationUrl
        }`}
        target="_blank"
        rel="noreferrer"
      >
        {registration.registrationFormName}
      </a>
    );
  } else if (registration?.registrationFormName && !shouldLinkRegistration) {
    registrationFormElem = <>{registration.registrationFormName}</>;
  }

  return (
    <div className="Grid Grid--fit Grid--withGutter">
      <div className="Grid-cell">
        <div className="u-pad">
          <div>
            {isSeasonArchived ? (
              <span className="u-fontSizeLg u-colorGrey">• </span>
            ) : (
              <span className="u-fontSizeLg u-colorPositive">• </span>
            )}
            {season?.program?.name}
          </div>
          {season?.name && (
            <a
              href={`${APP_URL}/organizations/${orgId}/programs/${season?.program.id}/seasons/${season?.id}/participants`}
            >
              {season.name}
            </a>
          )}
          {!isSeasonArchived && <div>{inviteToJoin(seasonParticipant, inviteToJoinLink)}</div>}
        </div>
      </div>
      <div className="Grid-cell">
        <div className="u-pad">
          <div>{teamName}</div>
          <div className="u-colorGrey">{seasonParticipant.division?.name}</div>
        </div>
      </div>
      <div className="Grid-cell">
        <p className="u-pad" data-testid="participantRoles">
          {participantRolesDisplay}
        </p>
      </div>
      <div className="Grid-cell">
        <p className="u-pad"></p>
        {registrationFormElem}
      </div>
      <div className="Grid-cell">
        {canEditParticipation && (
          <PopupAction
            direction={['down']}
            text="..."
            popupStyle={{ width: '180px' }}
            actions={[
              {
                callback: () => (window.location.href = editURL),
                text: (
                  <div>
                    <Icon name="edit" style={{ marginRight: '6px' }} />
                    Edit
                  </div>
                ),
              },
            ]}
          />
        )}
      </div>
    </div>
  );
};

export default ParticipationInfo;
